import React, { useState, useEffect, useRef } from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import classnames from 'classnames';
import AhaEditorFieldV2Controller from '../../controllers/AhaEditorFieldV2Controller';
import {
  AhaEditorV2Controller,
  DocumentState,
  type EditorPlugin,
} from '@aha-app/aha-editor-v2';
import styles from './DocumentSavedState.module.css';

const flakyStates = [DocumentState.NOT_CONNECTED, DocumentState.BLOCKED];
const FLAP_TIMEOUT = 30000;
const SAVING_TIMEOUT = 750;
const HIDE_TIMEOUT = 5000;

// Display an indicator when the document is saved.
const DocumentSavedState = () => {
  const controller = useController(AhaEditorFieldV2Controller);
  const editorController = useController(AhaEditorV2Controller);
  const { documentState } = controller.state;
  // The state that is currently shown.
  const [shownState, setShownState] = useState<string | null>(null);
  // Whether to actually display the state.
  const [shown, setShown] = useState(false);
  // Timers in refs so they persist across renders.
  const disconnectTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const savingTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const hideTimer = useRef<NodeJS.Timeout | undefined>(undefined);

  // Editor focus state.
  const {
    focused,
    pluginState: { isFullScreen },
  } = editorController.state;

  // Show the new state.
  const setCurrentState = newState => {
    clearTimeout(savingTimer.current);
    setShownState(newState);
    setShown(true);
    // Set a timer to hide the message after a while.
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      setShown(false);
    }, HIDE_TIMEOUT);
  };

  // React to changes to the document state.
  useEffect(() => {
    // Delay before signaling disconnection, so we can deal with flaky
    // connections more smoothly.
    if (flakyStates.includes(documentState)) {
      clearTimeout(disconnectTimer.current);
      disconnectTimer.current = setTimeout(() => {
        setCurrentState(documentState);
      }, FLAP_TIMEOUT);
      return;
    }

    // When the document state changes, show the indicator.
    // Saving happens so quickly that it feels a little strange to
    // have it happen immediately. Instead, we want to delay showing
    // it for some amount of time after the last state change came
    // in. This way, if you keep typing, we won't keep flickering
    // between "Saving…" and "Saved".
    if (documentState === DocumentState.SAVED) {
      clearTimeout(savingTimer.current);
      savingTimer.current = setTimeout(() => {
        setCurrentState(DocumentState.SAVED);
      }, SAVING_TIMEOUT);
      return;
    }

    setCurrentState(documentState);
  }, [documentState]);

  // Cleanup the timers.
  useEffect(() => {
    return () => {
      clearTimeout(disconnectTimer.current);
      clearTimeout(savingTimer.current);
      clearTimeout(hideTimer.current);
    };
  }, []);

  const className = classnames(styles.documentState, {
    [styles.hidden]: !shown || !shownState || !focused,
    [styles.fullScreen]: isFullScreen,
  });

  return <div className={className}>{shownState}</div>;
};

const DocumentSavedStatePlugin: EditorPlugin = {
  name: 'Document saved state plugin',
  layoutComponent: {
    location: 'below',
    Component: ApplicationView(DocumentSavedState),
  },
};

export default DocumentSavedStatePlugin;
